import { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { ZONES, EXPERIENCE, LICENSES } from "@/lib/extensions";
import { ChipGroup, RadioGroup } from "@/components/FormFields";
import { Upload } from "lucide-react";

const PICK = (base) => ({
  first_name: base.first_name || "", last_name: base.last_name || "", phone: base.phone || "",
  residence_city: base.residence_city || "", residence_region: base.residence_region || "", second_residence: base.second_residence || "",
  nautical_zones: base.nautical_zones || [], home_port: base.home_port || "",
  experience_level: base.experience_level || "", license: base.license || "",
});

export default function BaseProfileForm({ base, onSaved, submitLabel, testid = "base-profile" }) {
  const { t, lang } = useLang();
  const a = t.account;
  const [form, setForm] = useState(PICK(base));
  const [avatar, setAvatar] = useState(base.avatar_path || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/profile/avatar", fd);
    setAvatar(data.avatar_path);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone || !form.residence_city || !form.nautical_zones.length || !form.experience_level) {
      setErr(a.required); return;
    }
    setBusy(true); setErr("");
    try {
      const { data } = await api.put("/profile/base", form);
      onSaved?.(data);
    } catch { setErr("Error"); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-10" data-testid={`${testid}-form`}>
      <div className="flex items-center gap-5">
        <div className="h-20 w-20 rounded-full bg-[#0B192C] text-[#C2A878] font-serif text-2xl flex items-center justify-center overflow-hidden shrink-0">
          {avatar ? <span className="text-xs text-white/70">✓</span> : (form.first_name?.[0] || "B")}
        </div>
        <label className="btn-outline text-sm inline-flex items-center gap-2 cursor-pointer" data-testid={`${testid}-avatar`}>
          <Upload className="h-4 w-4" /> {a.photo}
          <input type="file" accept="image/*" className="hidden" onChange={onAvatar} />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className="label-elegant">{a.firstName} *</label><input className="input-elegant" value={form.first_name} onChange={set("first_name")} data-testid={`${testid}-first-name`} /></div>
        <div><label className="label-elegant">{a.lastName} *</label><input className="input-elegant" value={form.last_name} onChange={set("last_name")} data-testid={`${testid}-last-name`} /></div>
        <div><label className="label-elegant">{a.phone} *</label><input className="input-elegant" value={form.phone} onChange={set("phone")} placeholder="+34 600 000 000" data-testid={`${testid}-phone`} /></div>
        <div><label className="label-elegant">{a.city} *</label><input className="input-elegant" value={form.residence_city} onChange={set("residence_city")} data-testid={`${testid}-city`} /></div>
        <div><label className="label-elegant">{a.region}</label><input className="input-elegant" value={form.residence_region} onChange={set("residence_region")} data-testid={`${testid}-region`} /></div>
        <div><label className="label-elegant">{a.secondResidence}</label><input className="input-elegant" value={form.second_residence} onChange={set("second_residence")} data-testid={`${testid}-second`} /></div>
      </div>

      <div>
        <label className="label-elegant">{a.zones} *</label>
        <ChipGroup opts={ZONES} value={form.nautical_zones} onChange={(v) => setForm((f) => ({ ...f, nautical_zones: v }))} lang={lang} testid={`${testid}-zones`} />
        <input className="input-elegant mt-4 max-w-md" value={form.home_port} onChange={set("home_port")} placeholder={a.portPlaceholder} data-testid={`${testid}-port`} />
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <label className="label-elegant">{a.experience} *</label>
          <RadioGroup opts={EXPERIENCE} value={form.experience_level} onChange={(v) => setForm((f) => ({ ...f, experience_level: v }))} lang={lang} testid={`${testid}-experience`} />
        </div>
        <div>
          <label className="label-elegant">{a.license}</label>
          <select className="input-elegant" value={form.license} onChange={set("license")} data-testid={`${testid}-license`}>
            <option value="">—</option>
            {LICENSES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>

      {err && <p className="text-sm text-red-600" data-testid={`${testid}-error`}>{err}</p>}
      <div className="flex justify-end">
        <button disabled={busy} className="btn-primary disabled:opacity-40" data-testid={`${testid}-submit`}>{submitLabel || a.save}</button>
      </div>
    </form>
  );
}
